import { push } from 'connected-react-router';
import CoreAPIService from '../../../utils/core-api';
import { createSelector } from 'reselect';
import { fetchAction } from '../../actions';
import { PartnerBillById, SortOrder } from '../api-types';
import { paths } from '../../../routes/paths';

export type { PartnerBillById };

export const name: string = 'partnerBills';

export const types = {
    EDIT_PARTNER_BILL: name + '/EDIT_PARTNER_BILL',
    GET_PARTNER_BILL: name + '/GET_PARTNER_BILL',
    UPDATE_PARTNER_BILL_STATUS: name + '/UPDATE_PARTNER_BILL_STATUS',
    UPDATE_PARTNER_BILL_STATUSES: name + '/UPDATE_PARTNER_BILL_STATUSES',
    GET_PARTNER_TRANSACTIONS: name + '/GET_PARTNER_TRANSACTIONS',
    GET_PARTNER_TRANSACTIONS_ZIP: name + '/GET_PARTNER_TRANSACTIONS_ZIP',
    BULK_VALIDATE: name + '/BULK_VALIDATE',
    BULK_SEND: name + '/BULK_SEND',
    EXPORT_PAIEMENT: name + '/EXPORT_PAIEMENT',
};

export const partnerBillStatus = {
    PENDING: 'pending', // générée et à valider
    BLOCKED: 'blocked', // facture exclue
    VALIDATED: 'validated', // facture validée
    REPORTED: 'reported', // facture reportée (négative)
    SENT: 'sent', // facture payée et envoyée
};

function updatePartnerStatusAction(id, status) {
    return CoreAPIService.put(`/billing/partnerBills/${id}`, { status: status });
}

interface PartnerBillsState {
    partner_bills: PartnerBillById;
    partner_transactions: [];
}

const initialState: PartnerBillsState = {
    partner_bills: {} as PartnerBillById,
    partner_transactions: [],
};

const reducer = (state = initialState, action): PartnerBillsState => {
    if (action.scope) {
        return state;
    }
    const payload = action.payload;
    const parameters = action.parameters;
    const id = parameters ? parameters.id : 0;
    switch (action.type) {
        case types.GET_PARTNER_BILL:
            return {
                ...state,
                partner_bills: { ...state.partner_bills, [id]: payload },
            };
        case types.UPDATE_PARTNER_BILL_STATUS:
            return {
                ...state,
                partner_bills: { ...state.partner_bills, [id]: { ...state.partner_bills[id], ...payload.data } },
            };
        case types.GET_PARTNER_TRANSACTIONS:
            return {
                ...state,
                partner_transactions: payload.data,
            };
        case types.UPDATE_PARTNER_BILL_STATUSES:
            return state;
        default:
            return state;
    }
};
export default reducer;

export const actions = {
    getPartnerTransactions: () =>
        fetchAction(
            CoreAPIService.post(`/core/billing/partnerBills/search`, {
                filters: { status: partnerBillStatus.VALIDATED },
                order: SortOrder.ASC,
                sort: 'start_date',
            }),
            types.GET_PARTNER_TRANSACTIONS,
            {},
        ),
    getPartnerTransactionsZip: (amount, bills) =>
        fetchAction(
            CoreAPIService.post(`/core/partner_bills/all/transaction_files`, {
                max_amount: parseInt(amount, 10),
                bills,
            }),
            types.GET_PARTNER_TRANSACTIONS_ZIP,
            {},
        ),
    getPartnerBill: (id) =>
        fetchAction(CoreAPIService.get(`/core/billing/partnerBills/${id}`), types.GET_PARTNER_BILL, { id }),
    editPartnerBill: (id, payload) =>
        fetchAction(
            CoreAPIService.put(`/core/partner_bills/${id}`, payload),
            types.EDIT_PARTNER_BILL,
            { id },
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.billsPartner}/${id}`));
                },
            },
        ),
    updatePartnerBillStatus: (id, status, callback?) => {
        return fetchAction(
            updatePartnerStatusAction(id, status),
            types.UPDATE_PARTNER_BILL_STATUS,
            { id },
            {
                success: callback,
            },
        );
    },
    updatePartnerBillStatuses: (ids, status, callback?) => {
        let actions = Promise.resolve() as Promise<unknown>;
        for (let i = 0; i < ids.length; i++) {
            if (i) {
                actions = actions.then(
                    () =>
                        new Promise((resolve) => {
                            setTimeout(resolve, 100);
                        }),
                );
            }
            actions = actions.then(() => updatePartnerStatusAction(ids[i], status));
        }
        return fetchAction(
            actions,
            types.UPDATE_PARTNER_BILL_STATUSES,
            { ids, status },
            {
                success: callback,
            },
        );
    },
    bulkValidate: (payload: { start_date: string; end_date: string }, callback?) =>
        fetchAction(
            CoreAPIService.post(`/billing/partnerBills/week/validate`, payload),
            types.BULK_VALIDATE,
            {},
            { success: callback },
        ),
    bulkSend: (payload: { start_date: string; end_date: string }, callback?) =>
        fetchAction(
            CoreAPIService.post(`/billing/partnerBills/week/send`, payload),
            types.BULK_SEND,
            {},
            { success: callback },
        ),
    exportPaiement: (payload: { start_date: string; end_date: string }, callback?) =>
        fetchAction(
            CoreAPIService.post(`/billing/partnerBills/week/payment`, payload),
            types.EXPORT_PAIEMENT,
            {},
            { success: callback },
        ),
};

const localState = (state: any): PartnerBillsState => state[name] || initialState;
export const selectors = {
    partnerBills: (state) => localState(state).partner_bills,
    partnerTransaction: (state, props) => localState(state).partner_transactions,
};

const createListFromTransactions = (transactions) => {
    return transactions.reduce(function (result, item, index, array) {
        result[item.id] = item;
        return result;
    }, {});
};

export const makeMemoizedGetPartnerTransaction = () => {
    return createSelector(selectors.partnerTransaction, (transactions) => createListFromTransactions(transactions));
};
